// SC-2166 (FE layer): the agent occasionally drops raw 24-hex Mongo ObjectIds
// (finding ids) into user-facing prose ("Want me to apply the fix for finding
// 6a02...?"). Internal identifiers aren't user-facing copy. The BE prompt tighten
// is the source-side fix; this FE scrub is the durable belt-and-braces defense,
// run on RENDER of assistant text only (MarkdownText — never user text).
//
// Scope is ObjectIds specifically (an unambiguous 24-hex shape safe to strip).
// Business identifiers like SKUs or `ATH-NNNNN` record ids are deliberately NOT
// scrubbed — those are real values a user may legitimately want to see.

// EXACTLY 24 hex (Mongo ObjectIds are always 12 bytes = 24 chars). `{24,}` would
// also eat legitimate longer hex — commit SHAs (40), checksums (64), undashed
// UUIDs (32) — so pin the length; the surrounding `\b` keeps a 24-hex prefix of a
// longer token from matching either.

// The model often wraps an id in inline code or quotes ("finding `6ac9…`"). A
// wrapped id is matched WITH its wrapper, so a strip can't leave an empty "``" or
// '""' behind (SC-3422). Each wrapper is an explicit open/close pair because curly
// quotes don't close with the character they open with.
const ID = '[0-9a-f]{24}';
const WRAPPED_OR_BARE_ID = `(?:${[
  ['`', '`'],
  ['"', '"'],
  ["'", "'"],
  ['\u201c', '\u201d'], // “ ”
  ['\u2018', '\u2019'], // ‘ ’
]
  .map(([open, close]) => `${open}${ID}${close}`)
  .join('|')}|\\b${ID}\\b)`;

// "finding <id>" (or "finding id <id>") reads naturally as "this finding" — keep
// the sentence flowing rather than leaving a gap. Captures the word so its casing
// survives.
const NAMED_FINDING_ID = new RegExp(`\\b(finding)\\s+(?:ids?:?\\s+)?${WRAPPED_OR_BARE_ID}`, 'gi');
// Any other raw ObjectId (optionally introduced by "id:" / "ids ") is internal —
// strip it outright.
const BARE_OBJECT_ID = new RegExp(`(?:\\bids?:?\\s+)?${WRAPPED_OR_BARE_ID}`, 'gi');

export function scrubAssistantText(text: string): string {
  return (
    text
      .replace(NAMED_FINDING_ID, (_match, fragment: string) => `this ${fragment.toLowerCase()}`)
      .replace(BARE_OBJECT_ID, '')
      // Tidy only the gap a strip leaves: collapse a doubled space BETWEEN words
      // (leaves markdown line-break trailing spaces alone) and drop a space that
      // now sits before sentence punctuation.
      .replace(/(\S) {2,}(\S)/g, '$1 $2')
      .replace(/ +([.,!?;:])/g, '$1')
  );
}
